import { useMemo, useState } from "react";
import { api, formatApiError } from "@/api";
import { toast } from "sonner";
import { Send, X } from "lucide-react";

function nextMondayISO() {
    const d = new Date();
    const daysAhead = (8 - d.getDay()) % 7 || 7;
    d.setDate(d.getDate() + daysAhead);
    d.setHours(0, 0, 0, 0);
    return d.toISOString().slice(0, 10);
}

export default function SendWeeklyScheduleDialog({ open, onClose, members }) {
    const [selected, setSelected] = useState(new Set());
    const [weekStart, setWeekStart] = useState(nextMondayISO());
    const [sending, setSending] = useState(false);

    const emailable = useMemo(
        () => (members || []).filter(m => m.email && !m.email.endsWith("@scs.local")),
        [members]
    );
    const phoneOnly = useMemo(
        () => (members || []).filter(m => !m.email || m.email.endsWith("@scs.local")),
        [members]
    );

    const toggle = id => {
        setSelected(prev => {
            const next = new Set(prev);
            next.has(id) ? next.delete(id) : next.add(id);
            return next;
        });
    };
    const toggleAll = () => {
        if (selected.size === emailable.length) setSelected(new Set());
        else setSelected(new Set(emailable.map(m => m.id)));
    };

    const send = async () => {
        if (selected.size === 0) {
            toast.error("Pick at least one team member");
            return;
        }
        setSending(true);
        try {
            const res = await api.post("/team/send-weekly-schedule", {
                user_ids: [...selected],
                week_start: weekStart,
            });
            toast.success(`Weekly schedule sent to ${res.data.sent} team member${res.data.sent === 1 ? "" : "s"}`);
            if (res.data.skipped?.length) {
                toast.info(`Skipped ${res.data.skipped.length} (no email on file)`);
            }
            onClose();
            setSelected(new Set());
        } catch (e) {
            toast.error(formatApiError(e) || "Couldn't send the emails");
        } finally { setSending(false); }
    };

    if (!open) return null;

    return (
        <div
            className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/40 backdrop-blur-sm"
            data-testid="send-weekly-dialog"
        >
            <div className="bg-white rounded-2xl w-full max-w-lg shadow-xl overflow-hidden">
                <div className="p-6 border-b border-[var(--border-soft)] flex items-start justify-between">
                    <div>
                        <h2 className="font-display text-xl font-semibold">Send weekly schedule</h2>
                        <p className="text-sm text-[var(--text-secondary)] mt-1">
                            Emails each selected member their own jobs for the week. No emails are sent automatically — only when you tap Send below.
                        </p>
                    </div>
                    <button onClick={onClose} className="btn-ghost !p-2" aria-label="Close">
                        <X size={16} />
                    </button>
                </div>

                <div className="p-6 space-y-5">
                    <div>
                        <label className="text-xs uppercase tracking-[0.18em] text-[var(--text-muted)] font-medium">Week starting (Monday)</label>
                        <input
                            data-testid="send-weekly-week-start"
                            type="date"
                            className="input-field mt-1.5"
                            value={weekStart}
                            onChange={e => setWeekStart(e.target.value)}
                        />
                    </div>

                    <div>
                        <div className="flex items-center justify-between mb-2">
                            <label className="text-xs uppercase tracking-[0.18em] text-[var(--text-muted)] font-medium">
                                Team members ({selected.size} of {emailable.length})
                            </label>
                            {emailable.length > 0 && (
                                <button onClick={toggleAll} className="text-xs text-[var(--pine)] hover:underline"
                                        data-testid="send-weekly-toggle-all">
                                    {selected.size === emailable.length ? "Clear all" : "Select all"}
                                </button>
                            )}
                        </div>

                        {emailable.length === 0 ? (
                            <p className="text-sm text-[var(--text-muted)] italic p-4 border border-dashed border-[var(--border-soft)] rounded-lg">
                                Nobody on the team has an email on file — add one from the Team page first.
                            </p>
                        ) : (
                            <div className="space-y-1.5 max-h-60 overflow-y-auto pr-1">
                                {emailable.map(m => (
                                    <label
                                        key={m.id}
                                        data-testid={`send-weekly-member-${m.id}`}
                                        className="flex items-center gap-3 rounded-lg px-3 py-2 hover:bg-[var(--surface-soft)] cursor-pointer border border-transparent hover:border-[var(--border-soft)]"
                                    >
                                        <input
                                            type="checkbox"
                                            checked={selected.has(m.id)}
                                            onChange={() => toggle(m.id)}
                                            className="accent-[var(--pine)]"
                                        />
                                        <div className="min-w-0 flex-1">
                                            <div className="font-medium text-sm truncate">
                                                {m.name}
                                                {m.role === "manager" && <span className="text-[var(--text-muted)] text-xs ml-1">· manager</span>}
                                            </div>
                                            <div className="text-xs text-[var(--text-muted)] truncate">{m.email}</div>
                                        </div>
                                    </label>
                                ))}
                            </div>
                        )}

                        {phoneOnly.length > 0 && (
                            <p className="text-xs text-[var(--text-muted)] mt-3">
                                {phoneOnly.length} phone-only member{phoneOnly.length === 1 ? "" : "s"} can't receive emails — share their week by SMS instead.
                            </p>
                        )}
                    </div>
                </div>

                <div className="px-6 py-4 border-t border-[var(--border-soft)] flex justify-end gap-2 bg-[var(--surface-soft)]">
                    <button className="btn-ghost" onClick={onClose}>Cancel</button>
                    <button
                        data-testid="send-weekly-submit"
                        className="btn-primary inline-flex items-center gap-2 disabled:opacity-60 disabled:cursor-not-allowed"
                        disabled={sending || selected.size === 0}
                        onClick={send}
                    >
                        <Send size={14} />
                        {sending ? "Sending…" : `Send to ${selected.size || "…"}`}
                    </button>
                </div>
            </div>
        </div>
    );
}
